import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";

const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await forgotPassword(email);
    
    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans'] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg w-full max-w-md" data-testid="forgot-password-card">
          <Link 
            to="/connexion" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#19294C] mb-6 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </Link>

          {!submitted ? (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
                  Mot de passe oublié
                </h1>
                <p className="mt-2 text-slate-600">
                  Entrez votre email pour recevoir un lien de réinitialisation.
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className="pl-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white py-3 font-semibold"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Envoi..." : "Envoyer le lien"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-16 h-16 bg-[#00B8E4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#00B8E4]" />
              </div>
              <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
                Email envoyé !
              </h1>
              <p className="mt-4 text-slate-600">
                Si un compte existe avec cet email, vous recevrez un lien pour réinitialiser votre mot de passe.
              </p>
              <Link to="/connexion">
                <Button className="mt-6 bg-[#00B8E4] hover:bg-[#00a5ce] text-white">
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
